import { Component, DestroyRef, EventEmitter, forwardRef, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { GbrChildInput, GbrPersonInput, GbrPersonType } from '@coin/shared/gbr/util';
import { BooleanChoice } from '@coin/shared/core/util-enums';
import { Fn } from '@coin/shared/core/util-models';
import moment from 'moment';
import { filter } from 'rxjs/operators';
import { GbrPersonSaveService } from '../../../person-save.service';

@Component({
  selector: 'coin-siemens-energy-person-input',
  templateUrl: './person-input.component.html',
  styleUrls: ['./person-input.component.scss', '../../styles/gbr-data-point.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PersonInputComponent),
      multi: true
    }
  ],
  standalone: false
})
export class PersonInputComponent implements OnInit, ControlValueAccessor {
  private saveService = inject(GbrPersonSaveService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  @Input() type: GbrPersonType;
  @Input() display = false;
  @Output() remove = new EventEmitter<void>();

  public edit = false;
  public disabled = false;

  public yesNoOptions = [BooleanChoice.YES, BooleanChoice.NO];

  public formPerson: GbrPersonInput | GbrChildInput = {} as never;

  private selectedPerson: GbrPersonInput | GbrChildInput = null;
  set value(val: GbrPersonInput | GbrChildInput) {
    this.selectedPerson = val;

    if (this.selectedPerson) {
      this.formPerson = this.selectedPerson;
    }

    this.onChange(val);
  }

  public get value(): GbrPersonInput | GbrChildInput {
    return this.selectedPerson;
  }

  public get personTypes(): typeof GbrPersonType {
    return GbrPersonType;
  }

  public get isChildInput(): boolean {
    return this.type === GbrPersonType.Child;
  }

  public get today(): moment.Moment {
    return moment();
  }

  public get canSave(): boolean {
    return this.isChildInput
      ? !!(this.formPerson as GbrChildInput)?.birthday
      : !!(this.formPerson as GbrPersonInput)?.firstname && !!(this.formPerson as GbrPersonInput)?.lastname;
  }

  onChange: Fn = () => {};
  onTouch: Fn = () => {};

  constructor() {
    this.saveService.registerSaveListener(() => this.edit && this.save());
  }

  ngOnInit(): void {
    if (this.selectedPerson) {
      this.formPerson = this.selectedPerson;
    } else {
      this.setDefaultPerson();
    }
  }

  private setDefaultPerson(): void {
    if (!this.isChildInput) {
      this.formPerson = {
        gidOrEmail: '',
        firstname: '',
        lastname: '',
        location: {
          sameAsEmployee: true
        }
      };
    } else {
      this.formPerson = {
        sameHousehold: BooleanChoice.NO,
        inEducation: BooleanChoice.NO,
        birthday: null
      };
    }
  }

  public toggleEdit(): void {
    const isAddingSpouse = !this.edit && !this.value && this.type === GbrPersonType.Spouse;

    if (isAddingSpouse) {
      this.confirmSpouseEdit();
      return;
    }

    this.edit = !this.edit;
  }

  public removeClick(): void {
    this.remove.emit();
    this.setDefaultPerson();
  }

  public save(): void {
    this.writeValue(this.formPerson);
    this.onTouch(this.formPerson);
    this.edit = false;
  }

  private confirmSpouseEdit(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          headline: 'gbr.spouse-confirm-headline',
          translate: true,
          msg: 'gbr.spouse-confirm-body',
          confirmMsg: 'general.btnConfirm',
          cancelMsg: 'general.btnCancel'
        }
      })
      .afterClosed()
      .pipe(
        filter(confirmed => !!confirmed),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.edit = true;
      });
  }

  public writeValue(value: GbrPersonInput | GbrChildInput): void {
    this.value = value;
  }
  public registerOnChange(fn: Fn): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: Fn): void {
    this.onTouch = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
